import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { showSuccess, showError } from "@/lib/toastHelpers";

interface VoucherStatsDialogProps {
  open: boolean;
  onClose: () => void;
  voucherId?: string;
}

export function VoucherStatsDialog({ open, onClose, voucherId }: VoucherStatsDialogProps) {
  const queryClient = useQueryClient();

  const { data: stats, isLoading } = useQuery<any>({
    queryKey: queryKeys.vouchers.stats(voucherId ?? ''),
    queryFn: async () => {
      const { fetchAdminWorkspaceId } = await import('@/lib/adminWorkspaceHelper');
      const adminWorkspaceId = await fetchAdminWorkspaceId();
      if (!adminWorkspaceId) throw new Error('Admin workspace not found');
      return apiRequest('GET', `/api/vouchers/${voucherId}/stats`, undefined, {
        'x-workspace-id': adminWorkspaceId
      });
    },
    // Only fetch when both voucherId is known and the dialog is open
    enabled: !!voucherId && open
  });

  // Use useMutation so React manages the async lifecycle — avoids setState-on-unmounted-component
  const revokeMutation = useMutation({
    mutationFn: async (usageId: string) => {
      const { fetchAdminWorkspaceId } = await import('@/lib/adminWorkspaceHelper');
      const adminWorkspaceId = await fetchAdminWorkspaceId();
      if (!adminWorkspaceId) throw new Error('Admin workspace not found');
      return apiRequest('DELETE', `/api/vouchers/${voucherId}/usage/${usageId}`, undefined, {
        'x-workspace-id': adminWorkspaceId
      });
    },
    onSuccess: () => {
      showSuccess('Access revoked');
      // Only invalidate when voucherId is defined
      if (voucherId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.vouchers.stats(voucherId) });
      }
    },
    onError: (error: any) => {
      showError(error?.message || 'Failed to revoke');
    }
  });

  const handleRevoke = (usageId: string, userName: string) => {
    if (!confirm(`Revoke voucher access for ${userName}? This will expire their subscription.`)) return;
    revokeMutation.mutate(usageId);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Voucher Statistics</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : stats ? (
          <div className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.totalUses}</div>
                  <p className="text-sm text-muted-foreground">Total Uses</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">{stats.uniqueUsers}</div>
                  <p className="text-sm text-muted-foreground">Unique Users</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {stats.voucher?.maxUses
                      ? `${((stats.totalUses / stats.voucher.maxUses) * 100).toFixed(0)}%`
                      : '∞'}
                  </div>
                  <p className="text-sm text-muted-foreground">Usage Rate</p>
                </CardContent>
              </Card>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Recent Usage</h3>
              <div className="space-y-2">
                {stats.usages.map((usage: any) => (
                  <div key={usage._id} className="flex justify-between items-center p-3 border rounded">
                    <div>
                      <p className="font-medium">
                        {usage.user.firstName} {usage.user.lastName}
                      </p>
                      <p className="text-sm text-muted-foreground">{usage.user.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-sm">{new Date(usage.usedAt).toLocaleDateString()}</p>
                        <p className="text-sm text-muted-foreground">{new Date(usage.usedAt).toLocaleTimeString()}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        disabled={revokeMutation.isPending}
                        onClick={() => handleRevoke(usage._id, `${usage.user.firstName} ${usage.user.lastName}`)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
